import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { StatusBadge } from "@/components/StatusBadge";
import AiAnalysisView from "@/components/AiAnalysisView";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea,
} from "recharts";
import { Plus, Sparkles, Loader2, Trash2, FlaskConical, FileUp } from "lucide-react";

export default function LabTab({ patientId }) {
  const [catalog, setCatalog] = useState({});
  const [exams, setExams] = useState([]);
  const [trends, setTrends] = useState({});
  const [open, setOpen] = useState(false);
  const [panelKey, setPanelKey] = useState("");
  const [examDate, setExamDate] = useState(new Date().toISOString().slice(0, 10));
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(null);
  const [trendMarker, setTrendMarker] = useState("");
  const [importing, setImporting] = useState(false);
  const pdfRef = useRef();

  const importPdf = async (file) => {
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/patients/${patientId}/lab-exams/import-pdf`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${data.imported} exame(s) importado(s) do PDF`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setImporting(false);
      if (pdfRef.current) pdfRef.current.value = "";
    }
  };

  const load = () => {
    api.get(`/patients/${patientId}/lab-exams`).then((r) => setExams(r.data));
    api.get(`/patients/${patientId}/trends`).then((r) => setTrends(r.data));
  };
  useEffect(() => {
    api.get("/catalog").then((r) => setCatalog(r.data));
    load();
  }, [patientId]);

  const panel = catalog[panelKey];

  const save = async () => {
    if (!panelKey) { toast.error("Selecione um tipo de exame"); return; }
    const measurements = Object.entries(values)
      .filter(([, v]) => v !== "" && v != null)
      .map(([marker_key, v]) => ({ marker_key, value: parseFloat(v) }));
    if (!measurements.length) { toast.error("Preencha ao menos um valor"); return; }
    setSaving(true);
    try {
      await api.post(`/patients/${patientId}/lab-exams`, { panel_key: panelKey, exam_date: examDate, measurements });
      toast.success("Exame registrado");
      setOpen(false); setPanelKey(""); setValues({});
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const analyze = async (eid) => {
    setAnalyzing(eid);
    try {
      const { data } = await api.post(`/patients/${patientId}/lab-exams/${eid}/analyze`);
      setExams((prev) => prev.map((e) => (e.id === eid ? data : e)));
      toast.success("Análise concluída");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setAnalyzing(null); }
  };

  const remove = async (eid) => {
    await api.delete(`/patients/${patientId}/lab-exams/${eid}`);
    load();
    toast.success("Exame removido");
  };

  const trendKeys = Object.keys(trends);
  const activeTrendKey = trendMarker || trendKeys[0];
  const activeTrend = trends[activeTrendKey];
  const chartData = useMemo(
    () => (activeTrend?.points || []).map((p) => ({ date: p.date, valor: p.value })),
    [activeTrend]
  );

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-xl font-medium">Exames laboratoriais</h3>
        <div className="flex gap-2">
          <input ref={pdfRef} type="file" accept="application/pdf" hidden
            onChange={(e) => importPdf(e.target.files[0])} data-testid="lab-pdf-input" />
          <Button variant="outline" disabled={importing} onClick={() => pdfRef.current?.click()} data-testid="import-pdf-btn">
            {importing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileUp className="w-4 h-4 mr-2" />} Importar PDF
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-lab-btn"><Plus className="w-4 h-4 mr-2" /> Registrar exame</Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="lab-dialog">
            <DialogHeader><DialogTitle>Registrar exame laboratorial</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tipo de exame</Label>
                  <Select value={panelKey} onValueChange={(v) => { setPanelKey(v); setValues({}); }}>
                    <SelectTrigger data-testid="lab-panel-select"><SelectValue placeholder="Selecione" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(catalog).map(([k, p]) => (
                        <SelectItem key={k} value={k}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Data do exame</Label>
                  <Input type="date" value={examDate} onChange={(e) => setExamDate(e.target.value)} data-testid="lab-date-input" />
                </div>
              </div>
              {panel && (
                <div className="space-y-3 border-t border-border pt-3">
                  {panel.markers.map((m) => (
                    <div key={m.key} className="flex items-center gap-3">
                      <Label className="flex-1 text-sm font-normal">
                        {m.label} <span className="text-muted-foreground text-xs">({m.unit || "—"}) · ref {m.min}–{m.max}</span>
                      </Label>
                      <Input type="number" step="any" className="w-28 font-mono-data"
                        data-testid={`lab-value-${m.key}`}
                        value={values[m.key] ?? ""} onChange={(e) => setValues({ ...values, [m.key]: e.target.value })} />
                    </div>
                  ))}
                </div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={save} disabled={saving} data-testid="save-lab-btn">
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />} Salvar exame
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {trendKeys.length > 0 && (
        <Card className="p-6 border-border shadow-none">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-ai mb-1">Evolução</p>
              <h4 className="font-heading text-lg font-medium">{activeTrend?.label}</h4>
            </div>
            <Select value={activeTrendKey} onValueChange={setTrendMarker}>
              <SelectTrigger className="w-56" data-testid="trend-marker-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {trendKeys.map((k) => <SelectItem key={k} value={k}>{trends[k].label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
              <XAxis dataKey="date" stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
              <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
              {activeTrend && (
                <ReferenceArea y1={activeTrend.ref_min} y2={activeTrend.ref_max} fill="hsl(var(--success))" fillOpacity={0.08} />
              )}
              <Line type="monotone" dataKey="valor" stroke="hsl(var(--chart-2))" strokeWidth={2.5} dot={{ r: 4 }} />
            </LineChart>
          </ResponsiveContainer>
          <p className="text-xs text-muted-foreground mt-2">
            Faixa de referência destacada: {activeTrend?.ref_min}–{activeTrend?.ref_max} {activeTrend?.unit}
          </p>
        </Card>
      )}

      {exams.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground border-dashed">
          <FlaskConical className="w-8 h-8 mx-auto mb-3 opacity-40" />
          Nenhum exame laboratorial registrado.
        </Card>
      ) : (
        <div className="space-y-4">
          {exams.map((e) => (
            <Card key={e.id} className="p-6 border-border shadow-none" data-testid={`lab-exam-${e.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h4 className="font-heading text-lg font-medium">{e.panel_label}</h4>
                  <p className="text-sm text-muted-foreground">{e.category} · {e.exam_date}</p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={analyzing === e.id}
                    onClick={() => analyze(e.id)} data-testid={`analyze-lab-${e.id}`}>
                    {analyzing === e.id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2 text-ai" />}
                    {e.ai_analysis ? "Reanalisar" : "Analisar IA"}
                  </Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(e.id)} data-testid={`delete-lab-${e.id}`}>
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </div>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Marcador</TableHead>
                    <TableHead className="text-right">Valor</TableHead>
                    <TableHead>Referência</TableHead>
                    <TableHead className="text-right">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {e.measurements.map((m) => (
                    <TableRow key={m.marker_key}>
                      <TableCell className="font-medium">{m.label}</TableCell>
                      <TableCell className="text-right font-mono-data">{m.value} {m.unit}</TableCell>
                      <TableCell className="text-muted-foreground font-mono-data text-sm">{m.ref_min}–{m.ref_max}</TableCell>
                      <TableCell className="text-right"><StatusBadge status={m.status} /></TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {e.ai_analysis && <AiAnalysisView analysis={e.ai_analysis} />}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
